import { useRef, useCallback } from "react";
import type { MealKey } from "@/data/recipes";

interface UseDragSelectOptions {
  onSelect: (mealKey: MealKey, recipeId: string) => void;
  isSelected: (mealKey: MealKey, recipeId: string) => boolean;
  disabled?: boolean;
}

/**
 * Hook that enables drag-to-select behavior on a grid of items.
 * Only active for mouse (non-touch) interactions to avoid blocking mobile scroll.
 */
export function useDragSelect({ onSelect, isSelected, disabled }: UseDragSelectOptions) {
  const dragging = useRef(false);
  const dragMealKey = useRef<MealKey | null>(null);
  const dragMode = useRef<"select" | "deselect">("select");
  const visited = useRef(new Set<string>());

  const onPointerDown = useCallback((mealKey: MealKey, recipeId: string, e?: PointerEvent | React.PointerEvent) => {
    if (disabled) return;
    // Only enable drag-select for mouse; touch uses simple tap
    if (e && e.pointerType === "touch") return;
    dragging.current = true;
    dragMealKey.current = mealKey;
    dragMode.current = isSelected(mealKey, recipeId) ? "deselect" : "select";
    visited.current = new Set([recipeId]);
    onSelect(mealKey, recipeId);
  }, [onSelect, isSelected, disabled]);

  const onPointerEnter = useCallback((mealKey: MealKey, recipeId: string) => {
    if (!dragging.current || disabled) return;
    if (dragMealKey.current !== mealKey) return;
    if (visited.current.has(recipeId)) return;

    visited.current.add(recipeId);
    const selected = isSelected(mealKey, recipeId);

    if (dragMode.current === "select" && !selected) {
      onSelect(mealKey, recipeId);
    } else if (dragMode.current === "deselect" && selected) {
      onSelect(mealKey, recipeId);
    }
  }, [onSelect, isSelected, disabled]);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
    dragMealKey.current = null;
    visited.current.clear();
  }, []);

  return { onPointerDown, onPointerEnter, onPointerUp };
}
